// Marketing · Contactos: lista tipo Clientify con filtros de segmento, segmentos guardados,
// "Nunca contactados", acciones masivas y exportación CSV.
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Download, Filter, Layers, ListPlus, Save, Search, UserX } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDateTime } from "../../../lib/format.ts";
import { Button, Card, EmptyState, ErrorState, Input, Modal, Select, TableSkeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { FilterPanel } from "./FilterPanel.tsx";
import {
  cleanCriteria, criteriaCount, type Criteria, describeCriteria, downloadFile, errMsg, fullName, type MktContact, type MktList,
  relTime, StatusBadge, useMktOptions,
} from "./shared.tsx";

type BulkAction = "add_to_list" | "remove_from_list" | "add_tag" | "remove_tag" | "set_status" | "set_owner";
const SORTS = [
  { v: "created:desc", l: "Más recientes" }, { v: "created:asc", l: "Más antiguos" },
  { v: "name:asc", l: "Nombre A-Z" }, { v: "score:desc", l: "Mayor puntaje" }, { v: "status:asc", l: "Estado" },
];

export function ContactsPage(): JSX.Element {
  const qc = useQueryClient();
  const me = useMe();
  const isAdmin = Boolean(me.data?.admin);
  const opts = useMktOptions();

  const [criteria, setCriteria] = useState<Criteria>({});
  const [segmentName, setSegmentName] = useState<string | null>(null);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState("created:desc");
  const [showFilters, setShowFilters] = useState(false);
  const [showSegments, setShowSegments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);

  // Búsqueda con retardo.
  useEffect(() => { const t = setTimeout(() => setQ(qInput.trim()), 300); return () => clearTimeout(t); }, [qInput]);
  // Al cambiar filtros se reinicia la página y la selección.
  useEffect(() => { setPage(1); setSelected(new Set()); setAllMatching(false); }, [criteria, q, sort, pageSize]);

  const [sortKey, dir] = sort.split(":") as [string, string];
  const f = JSON.stringify(cleanCriteria(criteria));
  const list = useQuery<{ rows: MktContact[]; total: number }>({
    queryKey: ["mkt", "contacts", f, q, page, pageSize, sort],
    queryFn: () => api.get(`/mkt/contacts?page=${page}&pageSize=${pageSize}&sort=${sortKey}&dir=${dir}&q=${encodeURIComponent(q)}&f=${encodeURIComponent(f)}`),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const nFilters = criteriaCount(criteria);

  const target = (): { ids?: string[]; filter?: Criteria } =>
    allMatching ? { filter: cleanCriteria({ ...criteria, ...(q ? { q } : {}) }) } : { ids: [...selected] };
  const selCount = allMatching ? total : selected.size;
  const pageAllSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const togglePage = (): void => {
    setAllMatching(false);
    setSelected((prev) => {
      const n = new Set(prev);
      if (pageAllSelected) rows.forEach((r) => n.delete(r.id)); else rows.forEach((r) => n.add(r.id));
      return n;
    });
  };
  const toggleOne = (id: string): void => {
    setAllMatching(false);
    setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const bulk = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ affected: number }>("/mkt/contacts/bulk", { ...body, ...target() }),
    onSuccess: (r) => {
      toast.success(`Listo: ${r.affected} contacto(s) actualizados.`);
      setSelected(new Set()); setAllMatching(false);
      void qc.invalidateQueries({ queryKey: ["mkt"] });
    },
    onError: (e) => toast.error(errMsg(e, "No se pudo aplicar la acción.")),
  });

  const toggleNever = (): void => { setSegmentName(null); setCriteria((c) => cleanCriteria({ ...c, neverContacted: c.neverContacted ? undefined : true })); };

  const applySegment = async (id: string): Promise<void> => {
    try {
      const r = await api.get<{ list: MktList }>(`/mkt/lists/${id}`);
      setCriteria(cleanCriteria(r.list.criteria ?? {}));
      setSegmentName(r.list.name);
      setShowSegments(false);
    } catch (e) { toast.error(errMsg(e, "No se pudo cargar el segmento.")); }
  };

  const exportCsv = (): void => {
    const body = selCount > 0 ? target() : { filter: cleanCriteria({ ...criteria, ...(q ? { q } : {}) }) };
    void downloadFile("/mkt/contacts/export", `contactos_${new Date().toISOString().slice(0, 10)}.csv`, body);
  };

  const segments = useMemo(() => (opts.data?.lists ?? []).filter((l) => l.kind === "dynamic"), [opts.data]);
  const chips = describeCriteria(criteria, opts.data);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" aria-hidden />
          <Input className="pl-9" placeholder="Buscar nombre, email o teléfono" value={qInput} onChange={(e) => setQInput(e.target.value)} aria-label="Buscar contactos" />
        </div>
        <Button onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}>
          <Filter size={16} strokeWidth={1.75} /> Filtros{nFilters > 0 && <span className="badge bg-primary text-white ml-1">{nFilters}</span>}
        </Button>
        <Button onClick={toggleNever} aria-pressed={Boolean(criteria.neverContacted)} className={criteria.neverContacted ? "ring-2 ring-primary/40" : ""}>
          <UserX size={16} strokeWidth={1.75} /> Nunca contactados
        </Button>
        <div className="relative">
          <Button onClick={() => setShowSegments((v) => !v)} aria-expanded={showSegments}><Layers size={16} strokeWidth={1.75} /> Segmentos</Button>
          {showSegments && (
            <div className="absolute z-30 mt-1 w-72 card shadow-lg max-h-96 overflow-y-auto">
              {segments.length === 0 && <div className="px-3 py-2 text-sm text-ink-muted">Aún no hay segmentos guardados.</div>}
              {segments.map((s) => (
                <button key={s.id} className="w-full text-left px-3 py-2 text-sm hover:bg-surface-hover border-b border-line last:border-0 text-ink"
                  onClick={() => void applySegment(s.id)}>{s.name}</button>
              ))}
              {isAdmin && (
                <button className="w-full text-left px-3 py-2 text-sm text-primary dark:text-ink font-medium hover:bg-surface-hover flex items-center gap-2"
                  onClick={() => { setShowSegments(false); setSaving(true); }} disabled={nFilters === 0}>
                  <Save size={14} /> Guardar filtro actual como segmento{nFilters === 0 && " (sin filtros)"}
                </button>
              )}
            </div>
          )}
        </div>
        <Button onClick={exportCsv}><Download size={16} strokeWidth={1.75} /> Exportar</Button>
        <div className="flex-1" />
        <Select className="w-auto" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Ordenar">
          {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </Select>
      </div>

      {(chips.length > 0 || segmentName) && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {segmentName && <span className="badge bg-primary-soft text-primary dark:text-ink"><Layers size={12} className="mr-1" />{segmentName}</span>}
          {chips.map((c) => <span key={c} className="badge bg-surface-hover text-ink-secondary">{c}</span>)}
          <button className="link text-xs ml-1" onClick={() => { setCriteria({}); setSegmentName(null); }}>Quitar filtros</button>
        </div>
      )}

      {showFilters && (
        <Card className="p-4">
          <FilterPanel value={criteria} options={opts.data} onCancel={() => setShowFilters(false)}
            onApply={(c) => { setCriteria(c); setSegmentName(null); setShowFilters(false); }} />
        </Card>
      )}

      <SaveSegment open={saving} criteria={criteria} onClose={() => setSaving(false)}
        onSaved={(name) => { setSaving(false); setSegmentName(name); void qc.invalidateQueries({ queryKey: ["mkt"] }); }} />

      {selCount > 0 && (
        <BulkBar count={selCount} pending={bulk.isPending} options={opts.data} onRun={(b) => bulk.mutate(b)} onExport={exportCsv} isAdmin={isAdmin}
          canSelectAll={!allMatching && pageAllSelected && total > rows.length} total={total} onSelectAll={() => setAllMatching(true)}
          onClear={() => { setSelected(new Set()); setAllMatching(false); }} />
      )}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-line text-sm">
          <span className="text-ink-secondary"><span className="font-semibold text-ink tnum">{total.toLocaleString("es-CO")}</span> contactos</span>
          {list.isFetching && !list.isLoading && <span className="text-xs text-ink-muted">Actualizando…</span>}
        </div>
        {list.isLoading ? <TableSkeleton rows={8} cols={6} />
          : list.isError ? <ErrorState message={errMsg(list.error, "No se pudieron cargar los contactos.")} onRetry={() => void list.refetch()} />
          : rows.length === 0 ? <EmptyState title="Sin contactos" body="Ningún contacto cumple los filtros actuales." />
          : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-canvas">
                    <th className="th w-10"><input type="checkbox" checked={pageAllSelected} onChange={togglePage} aria-label="Seleccionar página" /></th>
                    <th className="th">Nombre</th>
                    <th className="th">Empresa</th>
                    <th className="th">Estado</th>
                    <th className="th text-right">Puntaje</th>
                    <th className="th">Propietario</th>
                    <th className="th">Creado</th>
                    <th className="th">Etiquetas</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={`hover:bg-surface-hover transition-colors ${selected.has(r.id) || allMatching ? "bg-primary-soft/60" : ""}`}>
                      <td className="td"><input type="checkbox" checked={allMatching || selected.has(r.id)} onChange={() => toggleOne(r.id)} aria-label={`Seleccionar ${fullName(r)}`} /></td>
                      <td className="td min-w-56">
                        <Link to={`/contacts/${r.id}`} className="font-medium text-ink hover:text-primary hover:underline">{fullName(r)}</Link>
                        <div className="text-xs text-ink-muted truncate max-w-72">
                          {r.email ?? "sin email"}{(r.mobile || r.phone) && ` · ${r.mobile ?? r.phone}`}
                          {r.unsubscribed && <span className="ml-1 badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">baja</span>}
                        </div>
                      </td>
                      <td className="td">{r.accountId ? <Link to={`/accounts/${r.accountId}`} className="hover:underline">{r.accountName}</Link> : <span className="text-ink-muted">—</span>}</td>
                      <td className="td"><StatusBadge status={r.mktStatus} /></td>
                      <td className="td text-right tnum font-medium text-ink">{r.mktScore}</td>
                      <td className="td whitespace-nowrap">{r.ownerName}</td>
                      <td className="td whitespace-nowrap" title={formatDateTime(r.createdAt)}>{relTime(r.createdAt)}</td>
                      <td className="td">
                        <div className="flex flex-wrap gap-1 max-w-56">
                          {r.tags.slice(0, 3).map((t) => <span key={t} className="badge bg-surface-hover text-ink-secondary">{t}</span>)}
                          {r.tags.length > 3 && <span className="text-xs text-ink-muted" title={r.tags.join(", ")}>+{r.tags.length - 3}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-secondary">
        <label className="flex items-center gap-2">Por página
          <Select className="w-auto py-1" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
        </label>
        <div className="flex items-center gap-2">
          <Button small disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Página anterior"><ChevronLeft size={14} /></Button>
          <span className="text-ink-muted tnum">Página {page} de {totalPages}</span>
          <Button small disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Página siguiente"><ChevronRight size={14} /></Button>
        </div>
      </div>
    </div>
  );
}

function SaveSegment({ open, criteria, onClose, onSaved }: { open: boolean; criteria: Criteria; onClose: () => void; onSaved: (name: string) => void }): JSX.Element | null {
  const [name, setName] = useState("");
  const save = useMutation({
    mutationFn: () => api.post("/mkt/lists", { name: name.trim(), kind: "dynamic", criteria: cleanCriteria(criteria) }),
    onSuccess: () => { toast.success("Segmento guardado."); onSaved(name.trim()); setName(""); },
    onError: (e) => toast.error(errMsg(e, "No se pudo guardar el segmento.")),
  });
  return (
    <Modal open={open} onClose={onClose} title="Guardar como segmento"
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>Guardar</Button></>}>
      <p className="text-sm text-ink-secondary mb-3">Un segmento es una lista dinámica: sus miembros se recalculan con estos filtros cada vez que se usa.</p>
      <Input autoFocus value={name} maxLength={120} placeholder="Nombre del segmento" onChange={(e) => setName(e.target.value)} aria-label="Nombre del segmento" />
    </Modal>
  );
}

function BulkBar(
  { count, total, pending, options, onRun, onExport, onClear, canSelectAll, onSelectAll, isAdmin }:
  { count: number; total: number; pending: boolean; options: ReturnType<typeof useMktOptions>["data"]; onRun: (b: Record<string, unknown>) => void;
    onExport: () => void; onClear: () => void; canSelectAll: boolean; onSelectAll: () => void; isAdmin: boolean },
): JSX.Element {
  const [action, setAction] = useState<BulkAction | "">("");
  const [val, setVal] = useState("");
  useEffect(() => setVal(""), [action]);
  const staticLists = (options?.lists ?? []).filter((l) => l.kind === "static");

  const run = (): void => {
    if (!action) return;
    const v = val.trim();
    if (action !== "set_status" && !v) { toast.error("Completa el valor de la acción."); return; }
    if (count > 100 && !window.confirm(`¿Aplicar a ${count} contactos?`)) return;
    const body: Record<string, unknown> = { action };
    if (action === "add_to_list" || action === "remove_from_list") body["listId"] = v;
    else if (action === "add_tag" || action === "remove_tag") body["tag"] = v;
    else if (action === "set_status") body["status"] = v || null;
    else body["ownerId"] = v;
    onRun(body);
  };

  return (
    <Card className="p-3 flex flex-wrap items-center gap-2 ring-1 ring-primary/30">
      <span className="text-sm font-medium text-ink"><span className="tnum">{count.toLocaleString("es-CO")}</span> seleccionados</span>
      {canSelectAll && <button className="link text-xs" onClick={onSelectAll}>Seleccionar los {total.toLocaleString("es-CO")} que cumplen el filtro</button>}
      <button className="link text-xs" onClick={onClear}>Limpiar</button>
      <div className="flex-1" />
      <Select className="w-auto" value={action} onChange={(e) => setAction(e.target.value as BulkAction | "")} aria-label="Acción masiva">
        <option value="">Acción masiva…</option>
        <option value="add_to_list">Añadir a lista</option>
        <option value="remove_from_list">Quitar de lista</option>
        <option value="add_tag">Poner etiqueta</option>
        <option value="remove_tag">Quitar etiqueta</option>
        <option value="set_status">Cambiar estado</option>
        <option value="set_owner">Cambiar propietario</option>
      </Select>
      {(action === "add_to_list" || action === "remove_from_list") && (
        <Select className="w-auto max-w-64" value={val} onChange={(e) => setVal(e.target.value)} aria-label="Lista">
          <option value="">Elige lista estática…</option>
          {staticLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </Select>
      )}
      {(action === "add_tag" || action === "remove_tag") && (
        <>
          <Input className="w-48" list="mkt-tags" maxLength={30} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Etiqueta" aria-label="Etiqueta" />
          <datalist id="mkt-tags">{(options?.tags ?? []).map((t) => <option key={t} value={t} />)}</datalist>
        </>
      )}
      {action === "set_status" && (
        <>
          <Input className="w-48" list="mkt-statuses" maxLength={40} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Estado (vacío = quitar)" aria-label="Estado" />
          <datalist id="mkt-statuses">{(options?.statuses ?? []).map((t) => <option key={t} value={t} />)}</datalist>
        </>
      )}
      {action === "set_owner" && (
        <Select className="w-auto max-w-64" value={val} onChange={(e) => setVal(e.target.value)} aria-label="Propietario">
          <option value="">Elige propietario…</option>
          {(options?.owners ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
      )}
      {action && <Button variant="primary" disabled={pending} onClick={run}><ListPlus size={16} /> {pending ? "Aplicando…" : "Aplicar"}</Button>}
      <Button onClick={onExport}><Download size={16} strokeWidth={1.75} /> Exportar selección</Button>
      {!isAdmin && <span className="w-full text-xs text-ink-muted">Las acciones masivas solo afectan a contactos de los que eres propietario.</span>}
    </Card>
  );
}
